<template>

    <headlessDialog open class="relative z-40">

        <div class="fixed inset-0 bg-black/50" aria-hidden="true"/>

        <div class="fixed inset-0 flex w-screen items-center justify-center">

            <DialogPanel
                    class="relative bg-white dark:bg-neutral-900 md:rounded max-w-md w-full h-full md:h-auto md:max-h-[80vh] overflow-hidden md:overflow-auto flex flex-col"
            >

                <div class="flex items-center justify-between p-4"
                     :class="[
                         $isStandalone ? '!pb-6': null,
                     ]"
                >

                    <Title class="mb-0">Exercise</Title>

                    <div class="flex items-center justify-between gap-x-6">
                        <div class="cursor-pointer" @click="handleTimerToggle"
                             v-if="user.settings.exerciseTimer && movement && !movement.trackDuration">
                            <Icon icon="stopwatch" class="text-2xl"/>
                        </div>
                        <div class="cursor-pointer" data-cy="cancel-exercise" @click="handleCancel">
                            <Icon icon="times" class="text-2xl"/>
                        </div>
                    </div>

                </div>

                <div v-if="activeRestTimer"
                     class="border-y border-brand-primary-500/60 bg-brand-primary-500/10 px-4 py-3"
                     data-cy="active-rest-timer-banner">
                    <div class="flex items-center justify-between gap-3">
                        <div class="min-w-0">
                            <div class="text-xs font-semibold uppercase text-brand-primary-500">
                                Rest Timer
                            </div>
                            <div class="truncate text-sm text-neutral-600 dark:text-neutral-300">
                                {{ activeRestTimerStatusLabel }}
                            </div>
                        </div>

                        <div class="shrink-0 font-mono text-2xl font-semibold" data-cy="active-rest-timer-remaining">
                            {{ $timerValue(activeRestTimer.remaining ?? activeRestTimer.duration ?? 0) }}
                        </div>
                    </div>

                    <div class="mt-3 grid grid-cols-4 gap-2">
                        <Button size="sm" secondary outlined @click="$emit('rest-timer-adjust', -15)">
                            -15s
                        </Button>
                        <Button size="sm" secondary outlined @click="$emit('rest-timer-adjust', 15)">
                            +15s
                        </Button>
                        <Button size="sm" secondary outlined @click="$emit('rest-timer-skip')">
                            Skip
                        </Button>
                        <Button size="sm"
                                :primary="activeRestTimer.status !== 'running'"
                                :secondary="activeRestTimer.status === 'running'"
                                outlined
                                @click="$emit('rest-timer-toggle')">
                            <Icon fixedWidth :icon="activeRestTimer.status === 'running' ? 'pause' : 'stopwatch'"/>
                        </Button>
                    </div>
                </div>

                <div class="relative flex-grow overflow-scroll scroll-bar-hidden border-y border-y-neutral-300 dark:border-y-neutral-800 p-4 min-h-[46vh]">

                    <Field>
                        <Label>Movement</Label>
                        <SelectInput :options="movementsAsOptions" v-model="modelValue.movementId"></SelectInput>
                    </Field>

                    <template v-if="movement">

                        <Field v-if="user.settings.exerciseTimer && (movement.trackDuration || timer)">
                            <Label>Timer</Label>
                            <Timer v-model="modelValue.duration"
                                   :metronome="true"
                                   :metronome-pattern="Number(user.settings.metronomePattern || 1)"
                                   :metronome-sound="user.settings.metronomeSound || 'classic'"
                            />
                            <div class="mt-3 py-1 text-center"
                                 data-cy="prev-duration"
                                 v-if="user.settings.metaPrevDuration && lastExercise && lastExercise.duration">
                                <div class="text-[10px] leading-tight uppercase text-neutral-500">Prev set</div>
                                <div class="mt-1 text-lg leading-none font-bold dark:text-white text-black">{{ $timerValue(lastExercise.duration) }}</div>
                            </div>
                        </Field>

                        <template v-if="movement.trackResistance">

                            <template v-if="movement.resistanceType === 'band'">

                                <Field>

                                    <Label>Bands
                                        {{ movement.resistanceConfig && movement.resistanceConfig.bandDoubled ? `(Doubled)` : '' }}
                                    </Label>

                                    <BandsInput ref="bandsInput" v-model="modelValue.bandIds"
                                                :lastExercise="lastExercise"
                                                @update:modelValue="handleBandIdsUpdate"/>

                                    <Help class="mt-3 text-center" v-if="lastExercise && lastExercise.bands.length && !this.modelValue.bandIds.length">
                                        Previous: {{ lastExercise.bands.map(band => band.brandAndName).join(', ') }}
                                    </Help>
                                </Field>

                                <Field class="flex items-center justify-between"
                                       v-if="user.settings.exerciseAdvancedToggle">
                                    <p>Advanced</p>
                                    <Switch v-model="modelValue.advanced"/>
                                </Field>

                            </template>


                            <Field v-else>
                                <Label>Resistance ({{ movement.resistanceType }} in {{ weightDisplayUnit }})</Label>
                                <NumberInputUnitAware v-model="modelValue.weight"
                                                      baseUnit="kg"
                                                      :displayUnit="weightDisplayUnit"
                                                      class="text-center text-4xl py-6"
                                                      placeholder="0"
                                />
                                <div class="mt-3 py-1 text-center" v-if="lastExercise && lastExercise.weight">
                                    <div class="text-[10px] leading-tight uppercase text-neutral-500">Prev set</div>
                                    <div class="mt-1 text-lg leading-none font-bold dark:text-white text-black">
                                        {{
                                            $numeral($convert(lastExercise.weight, 'kg').to(weightDisplayUnit)).format(weightDisplayFormat)
                                        }}
                                        {{ weightDisplayUnit }}
                                    </div>
                                </div>

                            </Field>

                        </template>

                        <template v-if="movement.trackRepetitions">

                            <div class="space-y-4 mb-6">

                                <Field class="w-full">
                                    <Label class="text-center">Full Repetitions</Label>
                                    <NumberInput placeholder="0"
                                                 class="text-center text-4xl py-6"
                                                 data-cy="exercise-repetitions"
                                                 v-model="modelValue.repetitions"/>

                                    <div v-if="hasPreviousReps || hasAllTimeMaxReps || hasPreviousWorkoutMaxReps"
                                         class="grid grid-flow-col auto-cols-fr divide-x divide-neutral-300 dark:divide-neutral-700 mt-3 py-1"
                                         data-cy="repetition-metadata">
                                        <div v-if="hasPreviousReps"
                                             class="px-2 text-center"
                                             data-cy="prev-reps">
                                            <div class="text-[10px] leading-tight uppercase text-neutral-500">Prev set</div>
                                            <div class="mt-1 text-lg leading-none font-bold dark:text-white text-black">{{ lastExercise.repetitions }}</div>
                                        </div>

                                        <div v-if="hasPreviousWorkoutMaxReps"
                                             class="px-2 text-center"
                                             data-cy="previous-workout-max-reps">
                                            <div class="text-[10px] leading-tight uppercase text-neutral-500">Prev workout max</div>
                                            <div class="mt-1 text-lg leading-none font-bold dark:text-white text-black">{{ previousWorkoutMaxExercise.repetitions }}</div>
                                        </div>

                                        <div v-if="hasAllTimeMaxReps"
                                             class="px-2 text-center"
                                             data-cy="all-time-max-reps">
                                            <div class="text-[10px] leading-tight uppercase text-neutral-500">All-time max</div>
                                            <div class="mt-1 text-lg leading-none font-bold dark:text-white text-black">{{ maxExercise.repetitions }}</div>
                                        </div>
                                    </div>
                                </Field>

                                <Field class="w-full" v-if="movement.trackRepetitionsPartial">
                                    <Label class="text-center">Partial Repetitions</Label>
                                    <NumberInput placeholder="0"
                                                 class="text-center text-4xl py-6"
                                                 data-cy="exercise-partial-repetitions"
                                                 v-model="modelValue.partialRepetitions"/>
                                    <div v-if="hasPreviousPartialReps || hasAllTimeMaxPartialReps || hasPreviousWorkoutMaxPartialReps"
                                         class="grid grid-flow-col auto-cols-fr divide-x divide-neutral-300 dark:divide-neutral-700 mt-3 py-1"
                                         data-cy="partial-repetition-metadata">
                                        <div v-if="hasPreviousPartialReps"
                                             class="px-2 text-center"
                                             data-cy="prev-partial-reps">
                                            <div class="text-[10px] leading-tight uppercase text-neutral-500">Prev set</div>
                                            <div class="mt-1 text-lg leading-none font-bold dark:text-white text-black">{{ lastExercise.partialRepetitions }}</div>
                                        </div>

                                        <div v-if="hasPreviousWorkoutMaxPartialReps"
                                             class="px-2 text-center"
                                             data-cy="previous-workout-max-partial-reps">
                                            <div class="text-[10px] leading-tight uppercase text-neutral-500">Prev workout max</div>
                                            <div class="mt-1 text-lg leading-none font-bold dark:text-white text-black">{{ previousWorkoutMaxPartialExercise.partialRepetitions }}</div>
                                        </div>

                                        <div v-if="hasAllTimeMaxPartialReps"
                                             class="px-2 text-center"
                                             data-cy="all-time-max-partial-reps">
                                            <div class="text-[10px] leading-tight uppercase text-neutral-500">All-time max</div>
                                            <div class="mt-1 text-lg leading-none font-bold dark:text-white text-black">{{ maxPartialExercise.partialRepetitions }}</div>
                                        </div>
                                    </div>
                                </Field>

                            </div>

                            <div v-if="user.settings.metaMaxRepsSource === 'any' && maxExerciseForBandHelp?.bands" class="-mt-3 mb-3">
                                <Help class="text-center font-bold dark:text-white text-black">
                                    <span class="text-neutral-500">Your Max Reps were performed with</span><br />
                                    {{ maxExerciseForBandHelp.bands.map(band => band.brandAndName).join(', ') }}
                                </Help>
                                <div class="text-center text-xs bg-brand-primary-100 text-neutral-950 rounded p-2 mt-6">
                                    You might want to update the Max Reps Source setting to use <span class="font-bold">Selected Band(s)</span>, which you can change in your
                                    <router-link to="/my/settings" class="text-brand-primary-500">settings</router-link>.
                                </div>
                            </div>


                        </template>

                        <Field v-if="user.settings.exerciseExhaustionLevel">
                            <div class="flex items-center justify-between">
                                <Label>Exhaustion Level</Label>
                                <button type="button"
                                        class="text-sm text-brand-primary-500"
                                        v-if="modelValue.exhaustionLevel"
                                        @click="handleExhaustionLevelClear">
                                    Clear
                                </button>
                            </div>

                            <div class="grid grid-cols-5 gap-2">
                                <button v-for="level in exhaustionLevels"
                                        :key="level"
                                        type="button"
                                        :data-cy="`exhaustion-level-${level}`"
                                        class="rounded border py-3 text-center font-semibold"
                                        :class="[
                                            modelValue.exhaustionLevel === level
                                              ? 'border-brand-primary-500 bg-brand-primary-100 text-neutral-950'
                                              : 'border-neutral-300 dark:border-neutral-700',
                                        ]"
                                        @click="handleExhaustionLevelSet(level)">
                                    {{ level }}
                                </button>
                            </div>

                            <div class="mt-3 py-1 text-center"
                                 data-cy="prev-exhaustion-level"
                                 v-if="lastExercise && lastExercise.exhaustionLevel !== undefined && lastExercise.exhaustionLevel !== null">
                                <div class="text-[10px] leading-tight uppercase text-neutral-500">Prev set</div>
                                <div class="mt-1 text-lg leading-none font-bold dark:text-white text-black">{{ lastExercise.exhaustionLevel }}</div>
                            </div>
                        </Field>

                        <Field v-if="user.settings.exerciseNotes">
                            <Label>Notes</Label>
                            <TextareaInput
                                    v-model="modelValue.notes"
                                    :rows="5"
                                    auto-resize
                                    data-cy="exercise-notes"
                            />
                        </Field>

                        <Field v-if="lastExercise && lastExercise.notes">
                            <div class="flex items-center justify-between">
                                <Label>Previous Notes</Label>
                                <button type="button"
                                        class="text-brand-primary-500"
                                        aria-label="Copy previous notes"
                                        data-cy="copy-previous-exercise-notes"
                                        @click="handleLastExerciseNotesCopy">
                                    <Icon icon="copy"/>
                                </button>
                            </div>
                            <p class="mt-3 max-h-24 overflow-y-auto bg-neutral-200 dark:bg-neutral-950 px-3 py-2 rounded whitespace-pre-line"
                               data-cy="previous-exercise-notes">
                                {{ lastExercise.notes }}
                            </p>
                        </Field>

                    </template>

                </div>

                <div class="flex items-center justify-between p-4">

                    <div>
                        <Button danger outlined @click="handleDelete" v-if="modelValue.id">
                            <Icon icon="trash-alt"/>
                        </Button>
                    </div>

                    <div>
                        <Button block primary data-cy="save-exercise" @click="handleSave">
                            Save
                        </Button>
                    </div>

                </div>

            </DialogPanel>

        </div>

    </headlessDialog>

</template>

<script>
import {mapActions} from 'vuex';

import {
  Dialog as headlessDialog,
  DialogPanel,
} from '@headlessui/vue';

import BandsInput from '@/components/BandsInput.vue';
import Metronome from '@/components/Metronome.vue';
import Timer from '@/components/Timer.vue';

export default {
  name: 'Form',
  components: {
    BandsInput,
    DialogPanel,
    headlessDialog,
    Metronome,
    Timer,
  },
  computed: {
    lastExercise() {
      return this.lastExerciseId ? this.$store.getters['exercise/findBy'](this.lastExerciseId, 'id') : null;
    },
    maxExercise() {
      return this.maxExerciseId ? this.$store.getters['exercise/findBy'](this.maxExerciseId, 'id') : null;
    },
    previousWorkoutMaxExercise() {
      return this.previousWorkoutMaxExerciseId
        ? this.$store.getters['exercise/findBy'](this.previousWorkoutMaxExerciseId, 'id')
        : null;
    },
    maxPartialExercise() {
      return this.maxPartialExerciseId
        ? this.$store.getters['exercise/findBy'](this.maxPartialExerciseId, 'id')
        : null;
    },
    previousWorkoutMaxPartialExercise() {
      return this.previousWorkoutMaxPartialExerciseId
        ? this.$store.getters['exercise/findBy'](this.previousWorkoutMaxPartialExerciseId, 'id')
        : null;
    },
    maxExerciseForBandHelp() {
      return this.showAllTimeMaxReps ? this.maxExercise : this.previousWorkoutMaxExercise;
    },
    hasPreviousReps() {
      return this.user.settings.metaPrevReps
        && this.lastExercise
        && this.lastExercise.repetitions !== undefined
        && this.lastExercise.repetitions !== null;
    },
    hasPreviousPartialReps() {
      return this.user.settings.metaPrevReps
        && this.lastExercise
        && this.lastExercise.partialRepetitions !== undefined
        && this.lastExercise.partialRepetitions !== null;
    },
    hasAllTimeMaxReps() {
      return this.showAllTimeMaxReps && Boolean(this.maxExercise?.repetitions);
    },
    hasPreviousWorkoutMaxReps() {
      return this.showPreviousWorkoutMaxReps && Boolean(this.previousWorkoutMaxExercise?.repetitions);
    },
    hasAllTimeMaxPartialReps() {
      return this.showAllTimeMaxReps && Boolean(this.maxPartialExercise?.partialRepetitions);
    },
    hasPreviousWorkoutMaxPartialReps() {
      return this.showPreviousWorkoutMaxReps && Boolean(this.previousWorkoutMaxPartialExercise?.partialRepetitions);
    },
    previousRepsSourceIsSelected() {
      const source = this.user.settings.metaPrevRepsSource ?? this.user.settings.meta_prev_reps_source ?? 'selected';

      return source === 'selected';
    },
    setPositionKey() {
      return [
        this.setIndex || '',
        this.setGroupIndex || '',
      ].join(':');
    },
    canShowMaxReps() {
      if (this.user.settings.metaMaxRepsSource === 'any') {
        return true;
      }

      if (this.user.settings.metaMaxRepsSource !== 'selected') {
        return false;
      }

      return this.modelValue.bandIds.length || this.movement?.resistanceType !== 'band';
    },
    showAllTimeMaxReps() {
      return this.user.settings.metaMaxReps && this.canShowMaxReps;
    },
    showPreviousWorkoutMaxReps() {
      const enabled = this.user.settings.metaPreviousWorkoutMaxReps
        ?? this.user.settings.meta_previous_workout_max_reps
        ?? true;

      return enabled && this.canShowMaxReps;
    },
    exhaustionLevels() {
      return [1, 2, 3, 4, 5];
    },
    activeRestTimerStatusLabel() {
      if (this.activeRestTimer?.status === 'paused') {
        return 'Paused';
      }

      return 'Resting while you set up';
    },
    movement() {
      return this.modelValue.movementId ? this.movements.find(movement => movement.id === this.modelValue.movementId) : null;
    },
    movements() {
      return this.$store.getters['movement/all'] || [];
    },
    movementsAsOptions() {
      return [
        {
          label: '- Select Movement -',
          value: null,
        },
        ...this.movements.sort((a, b) => a.name > b.name ? 1 : -1).map((movement) => {
          return {
            label: movement.name,
            value: movement.id,
          };
        }),
      ];
    },
    user() {
      return this.$store.getters['auth/user'];
    },
    weightDisplayFormat() {
      return this.user.systemOfMeasure === 'metric' ? '0,0.0' : '0,0';
    },
    weightDisplayUnit() {
      return this.user.systemOfMeasure === 'metric' ? 'kg' : 'lb';
    },
  },
  created() {
    if ((this.modelValue.duration > 0 || this.user.settings.autoOpenTimer) && this.movement?.trackDuration) {
      this.timer = true;
    }
  },
  data() {
    return {
      lastExerciseId: null,
      lastExerciseRequestNumber: 0,
      loading: false,
      maxExerciseId: null,
      maxExerciseRequestNumber: 0,
      maxPartialExerciseId: null,
      previousWorkoutMaxExerciseId: null,
      previousWorkoutMaxPartialExerciseId: null,
      timer: false,
    }
  },
  methods: {
    ...mapActions({
      exerciseLast: 'exercise/last',
      exerciseMax: 'exercise/max',
    }),
    handleCancel() {
      this.$emit('cancel');
    },
    handleDelete() {
      this.$emit('delete');
    },
    handleExhaustionLevelClear() {
      this.modelValue.exhaustionLevel = null;
    },
    handleExhaustionLevelSet(value) {
      this.modelValue.exhaustionLevel = value;
    },
    handleLastExerciseNotesCopy() {
      this.modelValue.notes = this.lastExercise.notes;
    },
    handleBandIdsUpdate(bandIds) {
      this.modelValue.bandIds = bandIds;
    },
    handleSave() {
      this.$emit('save');
    },
    handleTimerToggle() {
      this.timer = !this.timer;
    },
    loadLastExercise(bandIds = null) {
      const requestNumber = ++this.lastExerciseRequestNumber;
      this.loading = true;

      return this.exerciseLast({
        params: {
          exerciseId: this.modelValue.id || null,
          movementId: this.modelValue.movementId,
          rank: this.modelValue.rank,
          setGroupIndex: this.setGroupIndex,
          setIndex: this.setIndex,
          workoutId: this.modelValue.workoutId,
          bandIds: this.previousRepsSourceIsSelected ? (bandIds || this.modelValue.bandIds || []) : [],
        },
      }).then((ids) => {
        if (requestNumber === this.lastExerciseRequestNumber) {
          this.lastExerciseId = ids.length ? ids[0] : null;
        }
      }).finally(() => {
        if (requestNumber === this.lastExerciseRequestNumber) {
          this.loading = false;
        }
      });
    },
    loadMaxExercises(bandIds = null) {

      const requestNumber = ++this.maxExerciseRequestNumber;
      const params = {
        exerciseId: this.modelValue.id || null,
        movementId: this.modelValue.movementId,
        workoutId: this.modelValue.workoutId,
        bandIds: bandIds || this.modelValue.bandIds || [],
      };
      const requests = [];

      if (this.showAllTimeMaxReps) {
        requests.push(this.exerciseMax({
          params: {...params, timeframe: 'all_time'},
        }).then((ids) => {
          if (requestNumber === this.maxExerciseRequestNumber) {
            this.maxExerciseId = ids.length ? ids[0] : null;
          }
        }));
        if (this.movement?.trackRepetitionsPartial) {
          requests.push(this.exerciseMax({
            params: {...params, repetitionType: 'partial', timeframe: 'all_time'},
          }).then((ids) => {
            if (requestNumber === this.maxExerciseRequestNumber) {
              this.maxPartialExerciseId = ids.length ? ids[0] : null;
            }
          }));
        } else {
          this.maxPartialExerciseId = null;
        }
      } else {
        this.maxExerciseId = null;
        this.maxPartialExerciseId = null;
      }

      if (this.showPreviousWorkoutMaxReps) {
        requests.push(this.exerciseMax({
          params: {...params, timeframe: 'previous_workout'},
        }).then((ids) => {
          if (requestNumber === this.maxExerciseRequestNumber) {
            this.previousWorkoutMaxExerciseId = ids.length ? ids[0] : null;
          }
        }));
        if (this.movement?.trackRepetitionsPartial) {
          requests.push(this.exerciseMax({
            params: {...params, repetitionType: 'partial', timeframe: 'previous_workout'},
          }).then((ids) => {
            if (requestNumber === this.maxExerciseRequestNumber) {
              this.previousWorkoutMaxPartialExerciseId = ids.length ? ids[0] : null;
            }
          }));
        } else {
          this.previousWorkoutMaxPartialExerciseId = null;
        }
      } else {
        this.previousWorkoutMaxExerciseId = null;
        this.previousWorkoutMaxPartialExerciseId = null;
      }

      return Promise.all(requests);
    },
  },
  props: {
    activeRestTimer: {
      default: null,
      required: false,
    },
    modelValue: {
      required: true,
    },
    setGroupIndex: {
      default: null,
      required: false,
      type: Number,
    },
    setIndex: {
      default: null,
      required: false,
      type: Number,
    }
  },
  watch: {
    ['modelValue.bandIds']: {
      deep: true,
      async handler() {
        if (this.modelValue.movementId) {
          if (this.user.settings.metaPrevReps && this.previousRepsSourceIsSelected) {
            this.loadLastExercise();
          }

          this.loadMaxExercises();
        }
      },
    },
    ['modelValue.movementId']: {
      immediate: true,
      async handler() {
        if (this.modelValue.movementId) {

          await this.loadLastExercise().then(async () => {
            if (this.user.settings.autoLastBands && !this.modelValue.bandIds.length) {
              await this.$nextTick(() => {
                this.$refs.bandsInput?.handleBandsUsePrevious();
              });
            }
          });

          await this.$nextTick(() => {
            if (this.user.settings.metaMaxReps || this.user.settings.metaPreviousWorkoutMaxReps) {
              this.loadMaxExercises();
            }
          });
        }
      },
    },
    setPositionKey() {
      if (this.modelValue.movementId) {
        this.loadLastExercise();
      }
    },
  },
};
</script>

<style scoped lang="scss">

</style>
